import { useEffect, useRef, useState } from "react";
import { getSprintDados, getSprintHistorico } from "../../api/sprint.api";
import { getSetupSprint } from "../../api/setup.api";
import { getParam } from "../../utils/parametros";

const REFRESH_SEC = 60;

const FECHADOS = new Set([
  "Fechado", "Resolvido", "Em Teste", "Feedback",
  "Aguardando Testes", "Aguardando Versão", "Ajuste Especificação",
]);

const isProg = t =>
  t.programado === "1" || t.programado === 1 || t.programado === true ||
  t.programacao_tipo === 1 || t.programacao_tipo === 2;

const SIZE_H    = { P: 1, M: 9, G: 18, XG: 36 };
const SIZE_OPTS = ["P", "M", "G", "XG"];
function ticketH(t) { return SIZE_H[String(t.size || "").toUpperCase().trim()] ?? 0; }

const DEV_AVATAR_MAP = [
  { match: "guilherme", bg: "#712B13", color: "#F5C4B3" },
  { match: "paulo",     bg: "#0C447C", color: "#B5D4F4" },
  { match: "marcelo",   bg: "#085041", color: "#9FE1CB" },
  { match: "rudi",      bg: "#3C3489", color: "#CECBF6" },
  { match: "nelson",    bg: "#1C5040", color: "#A0DFC4" },
];
function devAv(nome) {
  if (!nome) return { bg: "#2C2C2A", color: "#B4B2A9" };
  const l = nome.toLowerCase();
  for (const m of DEV_AVATAR_MAP) if (l.includes(m.match)) return m;
  return { bg: "#2C2C2A", color: "#B4B2A9" };
}
function devInitials(nome) {
  if (!nome) return "?";
  const p = nome.trim().split(/\s+/);
  return p.length >= 2 ? (p[0][0] + p[1][0]).toUpperCase() : p[0][0].toUpperCase();
}

function parseDateLocal(str) {
  if (!str) return null;
  const [d] = str.split(" ");
  const [y, m, day] = d.split("-").map(Number);
  const dt = new Date(y, m - 1, day);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

function diasDecorridos(dataInicio) {
  if (!dataInicio) return null;
  const inicio = parseDateLocal(dataInicio);
  if (!inicio) return null;
  const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
  const diff = Math.floor((hoje - inicio) / 86400000);
  return diff < 1 ? 1 : diff;
}

/* Extrai o maior número encontrado num label como "PE.025_S0499" → 499 */
function sprintNum(label) {
  const nums = String(label).match(/\d+/g);
  if (!nums) return 0;
  return Math.max(...nums.map(Number));
}

/* Formata o label para exibição:
   "20260504" → "05/04"   (AAAAMMDD puro)
   "PE.025_S0499" → "499" (formato legado com prefixo)  */
function sprintLabel(raw) {
  const str = String(raw);
  if (/^\d{8}$/.test(str)) {
    /* AAAAMMDD → MM/DD */
    return `${str.slice(4, 6)}/${str.slice(6, 8)}`;
  }
  if (/^\d+$/.test(str)) return str;
  const nums = str.match(/\d{3,}/g);
  return nums ? nums[nums.length - 1] : str;
}

function normalizarHistorico(raw) {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw.map(item => {
    const saida = item.saida?.geral || item.indicadores?.saida?.geral
               || item.saida        || item.indicadores?.saida
               || item;
    const total   = saida.total     ?? saida.total_geral  ?? 0;
    const pct     = saida.percentual ?? 0;
    let fechado   = saida.fechado ?? saida.fechados ?? saida.closed ?? -1;
    if (fechado < 0) fechado = pct > 0 && total > 0 ? Math.round(pct * total / 100) : 0;
    const pctFinal = pct > 0 ? pct : (total > 0 ? Math.round(fechado / total * 100) : 0);
    const rawLabel = String(item.sprint || item.versao || item.grupo_entrega || "?");
    /* item.sprint é o ID sequencial da versão no Redmine — sempre crescente e cronológico */
    const sprintId = item.sprint ? Number(item.sprint) : sprintNum(rawLabel);
    return {
      label:   sprintLabel(rawLabel),
      sortKey: sprintId,
      total, fechado, pct: pctFinal,
    };
  }).sort((a, b) => a.sortKey - b.sortKey);
}

function pctColor(p) {
  return p >= 70 ? "#4ade80" : p >= 40 ? "#fbbf24" : "#f87171";
}

function buildDevStats(tickets) {
  const map = new Map();
  for (const t of tickets) {
    if (!isProg(t) || !t.desenvolvedor) continue;
    const nome = t.desenvolvedor;
    if (!map.has(nome)) map.set(nome, { nome, tickets: [] });
    map.get(nome).tickets.push(t);
  }
  return [...map.values()].sort((a, b) => b.tickets.length - a.tickets.length);
}

function calcStats(tickets, usarHoras, dias) {
  const total      = tickets.length;
  const fechados   = tickets.filter(t => FECHADOS.has(t.status)).length;
  const emCurso    = tickets.filter(t => t.status === "Em curso").length;
  const aguardando = total - fechados - emCurso;
  const horasFechadas = tickets.filter(t => FECHADOS.has(t.status)).reduce((s, t) => s + ticketH(t), 0);
  const horasTotal    = tickets.reduce((s, t) => s + ticketH(t), 0);
  const unidFech  = usarHoras ? horasFechadas : fechados;
  const unidTotal = usarHoras ? horasTotal    : total;
  const pct = unidTotal > 0 ? Math.round(unidFech / unidTotal * 100) : 0;
  const vel = dias && unidFech > 0 ? (unidFech / dias).toFixed(1) : null;
  const sizeDist = {};
  for (const opt of SIZE_OPTS) sizeDist[opt] = { total: 0, fechados: 0 };
  for (const t of tickets) {
    const s = String(t.size || "").toUpperCase().trim();
    if (sizeDist[s]) {
      sizeDist[s].total++;
      if (FECHADOS.has(t.status)) sizeDist[s].fechados++;
    }
  }
  return { total, fechados, emCurso, aguardando, horasFechadas, horasTotal, pct, vel, sizeDist };
}

/* ══════════════════════════════════════════════════
   Page
══════════════════════════════════════════════════ */
export default function Velocidade() {
  const [tickets,    setTickets]    = useState([]);
  const [sprint,     setSprint]     = useState(null);
  const [historico,  setHistorico]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(false);
  const [usarHoras,  setUsarHoras]  = useState(false);
  const [countdown,  setCountdown]  = useState(REFRESH_SEC);
  const loadRef = useRef(null);

  async function load(isRefresh = false) {
    if (!isRefresh) setLoading(true);
    setError(false);
    try {
      const [dados, histGeral, histProg, sprintCfg] = await Promise.all([
        getSprintDados(),
        getSprintHistorico().catch(() => []),
        getSprintHistorico("programado").catch(() => []),
        getSetupSprint().catch(() => null),
      ]);
      setTickets(Array.isArray(dados) ? dados : []);
      setSprint(sprintCfg);

      /* Usa o histórico geral (mais completo) e enriquece com dados de programado quando disponível */
      const normGeral = normalizarHistorico(histGeral);
      const normProg  = normalizarHistorico(histProg);
      const progMap   = new Map(normProg.map(s => [s.sortKey, s]));
      const merged    = normGeral.map(s => progMap.get(s.sortKey) ?? s);
      const qtd       = getParam("sprints_historico") || 4;
      setHistorico(merged.slice(-qtd));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  loadRef.current = load;

  useEffect(() => {
    loadRef.current();
    const id = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { loadRef.current(true); return REFRESH_SEC; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);

  if (loading) return (
    <div style={page}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, color: "#475569", fontSize: 14 }}>
        Carregando velocidade...
      </div>
    </div>
  );

  if (error) return (
    <div style={page}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, gap: 12 }}>
        <span style={{ fontSize: 32, color: "#fbbf24" }}>⚠</span>
        <p style={{ color: "#94a3b8" }}>Erro ao carregar dados</p>
        <button onClick={() => load()} style={btnSec}>Tentar novamente</button>
      </div>
    </div>
  );

  const dias     = diasDecorridos(sprint?.data_inicio);
  const devStats = buildDevStats(tickets);

  const progTickets   = tickets.filter(isProg);
  const totalTime     = progTickets.length;
  const fechadosTime  = progTickets.filter(t => FECHADOS.has(t.status)).length;
  const pctTime       = totalTime > 0 ? Math.round(fechadosTime / totalTime * 100) : 0;
  const horasTime     = progTickets.reduce((s, t) => s + ticketH(t), 0);
  const horasFechTime = progTickets.filter(t => FECHADOS.has(t.status)).reduce((s, t) => s + ticketH(t), 0);

  /* média de conclusão das últimas sprints (exceto a atual) */
  const histSemAtual    = historico.slice(0, -1);
  const mediaPct        = histSemAtual.length
    ? Math.round(histSemAtual.reduce((s, h) => s + h.pct, 0) / histSemAtual.length)
    : null;
  const mediaFechados   = histSemAtual.length
    ? Math.round(histSemAtual.reduce((s, h) => s + h.fechado, 0) / histSemAtual.length)
    : null;

  return (
    <div style={page}>

      {/* ── Header ── */}
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h1 style={h1}>Velocidade dos Desenvolvedores</h1>
          {sprint?.numero_sprint && (
            <span style={sprintPill}>Sprint {sprint.numero_sprint}</span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ display: "flex", borderRadius: 8, border: "1px solid rgba(96,165,250,0.15)", overflow: "hidden" }}>
            {[false, true].map(h => (
              <button key={String(h)} onClick={() => setUsarHoras(h)} style={{
                padding: "5px 12px", fontSize: 11, fontWeight: 700,
                background: usarHoras === h ? "rgba(96,165,250,0.18)" : "transparent",
                color: usarHoras === h ? "#60a5fa" : "#475569",
                border: "none", cursor: "pointer", transition: "all 0.15s",
              }}>
                {h ? "Horas" : "Tickets"}
              </button>
            ))}
          </div>
          <span style={{ fontSize: 11, color: "#334155" }}>↻ {countdown}s</span>
        </div>
      </header>

      {/* ── Corpo principal (2 colunas) ── */}
      <div style={{ display: "flex", gap: 16, flex: 1, minHeight: 0 }}>

        {/* ── Coluna esquerda: tendência ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14, width: 380, flexShrink: 0, minHeight: 0 }}>

          {/* KPIs do time */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <KpiCard label="Tickets programados" value={totalTime}        color="#94a3b8" />
            <KpiCard label="Concluídos"          value={fechadosTime}     color="#4ade80" />
            <KpiCard label="Conclusão sprint"    value={`${pctTime}%`}    color={pctColor(pctTime)} />
            <KpiCard label="Dias decorridos"     value={dias ?? "—"}      color="#fbbf24" />
            {mediaPct !== null && (
              <KpiCard label="Média de conclusão (sprints anteriores)" value={`${mediaPct}%`} color={pctColor(mediaPct)} wide />
            )}
            {mediaFechados !== null && (
              <KpiCard label="Média tickets fechados (sprints anteriores)" value={mediaFechados} color="#a78bfa" wide />
            )}
          </div>

          {/* Gráfico de tendência */}
          {historico.length > 1 && (
            <div style={{ ...glassCard, flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={sectionLabel}>Tendência por Sprint</span>
                <span style={{ fontSize: 9, color: "#334155" }}>tickets programados concluídos</span>
              </div>
              <TrendChart sprints={historico} />
            </div>
          )}
        </div>

        {/* ── Coluna direita: devs ── */}
        <div style={{ flex: 1, minWidth: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexShrink: 0 }}>
            <span style={sectionLabel}>Sprint Atual — Por Desenvolvedor</span>
            {horasTime > 0 && (
              <span style={{ fontSize: 11, color: "#475569" }}>
                {horasFechTime}h / {horasTime}h entregues
              </span>
            )}
          </div>
          {devStats.length === 0 ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, color: "#475569", fontSize: 13 }}>
              Nenhum ticket programado encontrado.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingBottom: 8 }}>
              {devStats.map(dev => (
                <DevCard key={dev.nome} dev={dev} dias={dias} usarHoras={usarHoras} mediaFechados={mediaFechados} />
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

/* ── TrendChart ───────────────────────────────────────────────────── */
function TrendChart({ sprints }) {
  const maxTotal = Math.max(...sprints.map(s => s.total), 1);

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: 6, minHeight: 0 }}>

      {/* Barras */}
      <div style={{ flex: 1, display: "flex", alignItems: "flex-end", gap: 6, minHeight: 0 }}>
        {sprints.map((s, i) => {
          const isCurrent = i === sprints.length - 1;
          const heightPct = s.total > 0 ? (s.total / maxTotal) * 100 : 0;
          const fechPct   = s.total > 0 ? (s.fechado / s.total) * 100 : 0;
          const cor       = pctColor(s.pct);
          return (
            <div
              key={s.label}
              title={`Sprint ${s.label}: ${s.fechado}/${s.total} (${s.pct}%)`}
              style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, height: "100%" }}
            >
              {/* % no topo */}
              <div style={{ fontSize: 9, color: cor, fontWeight: 700, flexShrink: 0 }}>
                {s.pct}%
              </div>
              {/* coluna */}
              <div style={{ flex: 1, width: "100%", display: "flex", alignItems: "flex-end" }}>
                <div style={{
                  width: "100%",
                  height: `${heightPct}%`,
                  borderRadius: "4px 4px 0 0",
                  background: "rgba(255,255,255,0.06)",
                  border: isCurrent ? `1px solid ${cor}40` : "1px solid rgba(96,165,250,0.08)",
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: isCurrent ? `0 0 10px ${cor}20` : undefined,
                  minHeight: 4,
                }}>
                  {/* parte fechada */}
                  <div style={{
                    position: "absolute", bottom: 0, left: 0, right: 0,
                    height: `${fechPct}%`,
                    background: isCurrent
                      ? `linear-gradient(180deg, ${cor}90, ${cor})`
                      : `${cor}55`,
                    transition: "height 0.4s ease",
                    minHeight: s.fechado > 0 ? 2 : 0,
                  }} />
                </div>
              </div>
              {/* total no rodapé */}
              <div style={{ fontSize: 9, color: "#334155", flexShrink: 0 }}>{s.fechado}/{s.total}</div>
            </div>
          );
        })}
      </div>

      {/* Labels das sprints */}
      <div style={{ display: "flex", gap: 6 }}>
        {sprints.map((s, i) => {
          const isCurrent = i === sprints.length - 1;
          return (
            <div key={s.label} style={{
              flex: 1, textAlign: "center", fontSize: 9,
              color: isCurrent ? "#60a5fa" : "#334155",
              fontWeight: isCurrent ? 700 : 400,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {isCurrent ? `${s.label} ●` : s.label}
            </div>
          );
        })}
      </div>

      {/* Legenda */}
      <div style={{ display: "flex", gap: 14, justifyContent: "center", flexShrink: 0, paddingTop: 4, borderTop: "1px solid rgba(255,255,255,0.04)" }}>
        <LegendItem color="#4ade80" label="≥70% concluído" />
        <LegendItem color="#fbbf24" label="40–69%" />
        <LegendItem color="#f87171" label="<40%" />
      </div>
    </div>
  );
}

function LegendItem({ color, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <div style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
      <span style={{ fontSize: 9, color: "#334155" }}>{label}</span>
    </div>
  );
}

/* ── DevCard ──────────────────────────────────────────────────────── */
function DevCard({ dev, dias, usarHoras, mediaFechados }) {
  const av  = devAv(dev.nome);
  const st  = calcStats(dev.tickets, usarHoras, dias);
  const cor = pctColor(st.pct);

  const vsMedia = mediaFechados !== null
    ? st.fechados - mediaFechados
    : null;

  return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>

        {/* Avatar */}
        <div style={{
          width: 38, height: 38, borderRadius: "50%",
          background: av.bg, color: av.color,
          fontSize: 13, fontWeight: 800, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {devInitials(dev.nome)}
        </div>

        {/* Nome + meta */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "#e2e8f0", fontWeight: 700, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {dev.nome}
            </span>
            {vsMedia !== null && vsMedia !== 0 && (
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 99,
                background: vsMedia > 0 ? "rgba(74,222,128,0.1)" : "rgba(248,113,113,0.1)",
                border: `1px solid ${vsMedia > 0 ? "rgba(74,222,128,0.25)" : "rgba(248,113,113,0.25)"}`,
                color: vsMedia > 0 ? "#4ade80" : "#f87171", flexShrink: 0,
              }}>
                {vsMedia > 0 ? "+" : ""}{vsMedia} vs média
              </span>
            )}
          </div>
          <div style={{ color: "#475569", fontSize: 10, marginTop: 2 }}>
            {st.total} ticket{st.total !== 1 ? "s" : ""} programados
            {dias && st.vel && (
              <span style={{ marginLeft: 8, color: "#60a5fa" }}>
                · {st.vel} {usarHoras ? "h" : "tickets"}/dia
              </span>
            )}
          </div>
        </div>

        {/* % conclusão */}
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ color: cor, fontWeight: 800, fontSize: 22 }}>{st.pct}%</div>
          <div style={{ color: "#334155", fontSize: 9, textTransform: "uppercase", letterSpacing: 0.5 }}>concluído</div>
        </div>
      </div>

      {/* Barra */}
      <div style={{ position: "relative", height: 6, borderRadius: 4, background: "rgba(255,255,255,0.06)", overflow: "hidden" }}>
        <div style={{
          height: "100%", borderRadius: 4, width: `${st.pct}%`,
          background: cor === "#4ade80"
            ? "linear-gradient(90deg, #4ade80, #22d3ee)"
            : cor === "#fbbf24"
              ? "linear-gradient(90deg, #fbbf24, #f59e0b)"
              : "linear-gradient(90deg, #f87171, #fb923c)",
          transition: "width 0.4s ease",
        }} />
      </div>

      {/* Status pills + size dist */}
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
        <div style={{ display: "flex", gap: 6, flex: 1 }}>
          <StatPill label="Concluídos"        value={st.fechados}   color="#4ade80" />
          <StatPill label="Em Desenvolvimento" value={st.emCurso}    color="#fbbf24" />
          <StatPill label="Aguardando"         value={st.aguardando} color="#60a5fa" />
        </div>

        {/* size badges */}
        {Object.values(st.sizeDist).some(d => d.total > 0) && (
          <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
            {SIZE_OPTS.map(opt => {
              const d = st.sizeDist[opt];
              if (!d.total) return null;
              return (
                <div key={opt} style={{
                  padding: "4px 7px", borderRadius: 6, textAlign: "center",
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(96,165,250,0.1)",
                }}>
                  <div style={{ fontSize: 9, color: "#60a5fa", fontWeight: 800 }}>{opt}</div>
                  <div style={{ fontSize: 11, color: "#e2e8f0", fontWeight: 700 }}>{d.fechados}/{d.total}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* horas (só no modo horas) */}
      {usarHoras && st.horasTotal > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#475569", borderTop: "1px solid rgba(255,255,255,0.04)", paddingTop: 8 }}>
          <span>Horas estimadas: <strong style={{ color: "#94a3b8" }}>{st.horasTotal}h</strong></span>
          <span>Horas entregues: <strong style={{ color: "#4ade80" }}>{st.horasFechadas}h</strong></span>
        </div>
      )}
    </div>
  );
}

/* ── Helpers visuais ──────────────────────────────────────────────── */
function KpiCard({ label, value, color, wide }) {
  return (
    <div style={{
      gridColumn: wide ? "1 / -1" : undefined,
      padding: "10px 14px", borderRadius: 10,
      background: "rgba(255,255,255,0.025)",
      border: "1px solid rgba(96,165,250,0.08)",
      display: "flex", flexDirection: "column", gap: 3,
    }}>
      <div style={{ color, fontWeight: 800, fontSize: 20 }}>{value}</div>
      <div style={{ color: "#334155", fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</div>
    </div>
  );
}

function StatPill({ label, value, color }) {
  return (
    <div style={{
      flex: 1, padding: "5px 6px", borderRadius: 7,
      background: `${color}0d`, border: `1px solid ${color}22`,
      display: "flex", flexDirection: "column", alignItems: "center", gap: 1,
    }}>
      <span style={{ color, fontWeight: 800, fontSize: 15 }}>{value}</span>
      <span style={{ color: "#334155", fontSize: 8, textTransform: "uppercase", letterSpacing: 0.4, textAlign: "center", lineHeight: 1.2 }}>{label}</span>
    </div>
  );
}

/* ── Estilos ──────────────────────────────────────────────────────── */
const page = {
  display: "flex", flexDirection: "column", gap: 16,
  height: "100vh", padding: "24px 28px", overflow: "hidden",
  background: "linear-gradient(135deg, #060d1e 0%, #080f1f 50%, #050b18 100%)",
  fontFamily: "Inter, sans-serif",
};

const h1 = {
  color: "#e2e8f0", fontSize: 18, fontWeight: 800, margin: 0, letterSpacing: -0.3,
};

const sprintPill = {
  fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 99,
  background: "rgba(96,165,250,0.12)", border: "1px solid rgba(96,165,250,0.2)", color: "#60a5fa",
};

const glassCard = {
  padding: "14px 16px", borderRadius: 12,
  background: "rgba(255,255,255,0.025)",
  border: "1px solid rgba(96,165,250,0.1)",
};

const card = {
  padding: "14px 16px", borderRadius: 12,
  background: "rgba(255,255,255,0.025)",
  border: "1px solid rgba(96,165,250,0.1)",
  display: "flex", flexDirection: "column", gap: 10,
};

const sectionLabel = {
  fontSize: 10, fontWeight: 700, color: "#475569",
  textTransform: "uppercase", letterSpacing: 0.8,
};

const btnSec = {
  padding: "8px 16px", borderRadius: 8, cursor: "pointer",
  background: "transparent", border: "1px solid rgba(96,165,250,0.2)",
  color: "#60a5fa", fontSize: 13, fontWeight: 600,
};
